// ==========================================================================
// Location Picker - Shared confirm + map sheets for saving the user location
// Extracted from the onboarding LocationSetup page so the same flow can be
// reopened elsewhere (e.g. the Weather tab) without duplicating logic.
//
// Google Maps APIs:
// - google.maps.marker.AdvancedMarkerElement
// - google.maps.Geocoder (reverse geocode on pin drop)
//
// Flow:
// - If user HAS location: Confirm sheet → CONFIRM or CHANGE → Map sheet
// - If user has NO location: Map sheet → CONTINUE or CANCEL
// ==========================================================================

import { BottomSheet } from '@/components/ui/BottomSheet';
import { useUpdateLocation, useUser } from '@/hooks/useUser';
import { useWeather } from '@/hooks/useWeather';
import { isGoogleMapsLoaded, loadGoogleMaps } from '@/lib/googleMaps';
import { track } from '@/lib/mixpanel';
import { showToast } from '@/stores/toastStore';
import { getWeatherEmoji } from '@/utils/weatherIcons';
import { MapPin, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LocationData {
  county: string;
  lat: number;
  lng: number;
}

interface PlaceSuggestion {
  placeId: string;
  text: string;
}

type ModalState = 'closed' | 'confirm' | 'map' | 'saved';

export interface LocationPickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after the location has been saved successfully (mutation resolved). */
  onSaved?: () => void;
  /** Which sheet to show first. Default: 'confirm' if the user has a saved location, else 'map'. */
  initialView?: 'confirm' | 'map';
  /**
   * 'onboarding' (default): original onboarding copy and flow.
   * 'update': neutral copy for changing an existing location (e.g. from the
   * Weather tab), ending in a "Location updated" success sheet.
   */
  variant?: 'onboarding' | 'update';
}

export function LocationPicker({ isOpen, onClose, onSaved, initialView, variant = 'onboarding' }: LocationPickerProps) {
  const { t } = useTranslation();

  const { data: user } = useUser();
  const { data: weather } = useWeather(!!user?.verified);
  const updateLocationMutation = useUpdateLocation();

  const fsp = user?.fsp;
  const primaryColor = fsp?.primary_color || '#ED974F';

  const [modalState, setModalState] = useState<ModalState>('closed');
  const [previousModalState, setPreviousModalState] = useState<ModalState>('closed');
  const [location, setLocation] = useState<LocationData | null>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(isGoogleMapsLoaded());
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isLocating, setIsLocating] = useState(false);
  const [locationPermission, setLocationPermission] = useState<'prompt' | 'granted' | 'denied'>('prompt');

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const userHasLocation = !!(user?.location?.lat && user?.location?.long);

  // Open/close the sheets with the isOpen prop. Opening picks the initial
  // sheet: explicit initialView, otherwise confirm when the user already has
  // a saved location, map when they don't.
  useEffect(() => {
    if (!isOpen) {
      setModalState('closed');
      return;
    }
    const view: ModalState = initialView ?? (userHasLocation ? 'confirm' : 'map');
    if (view === 'map' && !userHasLocation) {
      setLocation(null);
    }
    setPreviousModalState('closed');
    setModalState(view);
    // Only re-evaluate when the picker is opened/closed — location/user
    // updates while open must not reset the sheet the user is on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Check geolocation permission state
  useEffect(() => {
    if (!navigator.permissions) return;
    let permStatus: PermissionStatus | undefined;
    let cancelled = false;
    const onChange = () => {
      if (permStatus) {
        setLocationPermission(permStatus.state as 'prompt' | 'granted' | 'denied');
      }
    };
    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      if (cancelled) return;
      permStatus = status;
      setLocationPermission(status.state as 'prompt' | 'granted' | 'denied');
      status.addEventListener('change', onChange);
    });
    return () => {
      cancelled = true;
      permStatus?.removeEventListener('change', onChange);
    };
  }, []);

  // Load Google Maps lazily — only once the picker is opened, so mounting
  // this component (e.g. on the Weather tab) doesn't fetch the Maps script.
  useEffect(() => {
    if (isOpen && !isGoogleLoaded) {
      loadGoogleMaps()
        .then(() => setIsGoogleLoaded(true))
        .catch(() => showToast('Failed to load maps', 'error'));
    }
  }, [isOpen, isGoogleLoaded]);

  // Initialize geocoder
  useEffect(() => {
    if (isGoogleLoaded && !geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }
  }, [isGoogleLoaded]);

  // Set initial location from user data
  useEffect(() => {
    if (user?.location?.lat && user?.location?.long) {
      setLocation({
        county: user.location.municipality || 'Your area',
        lat: parseFloat(String(user.location.lat)),
        lng: parseFloat(String(user.location.long)),
      });
    }
  }, [user]);

  // Initialize map when modal opens
  useEffect(() => {
    if (modalState === 'map' && isGoogleLoaded && mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    }
  }, [modalState, isGoogleLoaded]);

  // Cleanup map and search when modal closes
  useEffect(() => {
    if (modalState !== 'map') {
      cleanupMap();
      setSearchValue('');
      setSuggestions([]);
      setIsLocating(false);
    }
  }, [modalState]);

  const placeMarker = (position: google.maps.LatLngLiteral) => {
    if (!mapInstanceRef.current) return;

    if (!markerRef.current) {
      const { AdvancedMarkerElement } = google.maps.marker;
      markerRef.current = new AdvancedMarkerElement({
        position,
        map: mapInstanceRef.current,
        gmpDraggable: true,
        title: 'Your location',
      });

      // Handle marker drag
      markerRef.current.addListener('dragend', () => {
        const pos = markerRef.current?.position;
        if (pos) {
          const lat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
          const lng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
          if (lat !== undefined && lng !== undefined) {
            reverseGeocode(Number(lat), Number(lng));
          }
        }
      });
    } else {
      markerRef.current.position = position;
    }

    mapInstanceRef.current.setCenter(position);
    mapInstanceRef.current.setZoom(15);
  };

  const initializeMap = () => {
    if (!mapRef.current || !isGoogleLoaded) return;

    const hasLocation = !!location;
    const center = hasLocation
      ? { lat: location.lat, lng: location.lng }
      : { lat: 0, lng: 20 };

    // Create map with mapId (required for AdvancedMarkerElement)
    mapInstanceRef.current = new google.maps.Map(mapRef.current, {
      center,
      zoom: hasLocation ? 15 : 2,
      disableDefaultUI: true,
      zoomControl: true,
      mapId: 'YAYA_MAP_ID',
    });

    // If user already has a location, place marker immediately
    if (hasLocation) {
      placeMarker({ lat: location.lat, lng: location.lng });
    }

    // Handle map click - place/move marker
    mapInstanceRef.current.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        placeMarker(pos);
        reverseGeocode(pos.lat, pos.lng);
      }
    });

  };

  const cleanupMap = () => {
    if (markerRef.current) {
      markerRef.current.map = null;
      markerRef.current = null;
    }
    mapInstanceRef.current = null;
  };

  const reverseGeocode = useCallback((lat: number, lng: number) => {
    if (!geocoderRef.current) return;

    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        let locationName = '';

        for (const result of results) {
          const addressComponents = result.address_components;
          const resultTypes = result.types || [];

          if (!locationName && resultTypes.includes('street_address')) {
            locationName = result.formatted_address.split(',')[0];
            break;
          }

          if (!locationName && resultTypes.includes('route')) {
            locationName = result.formatted_address.split(',')[0];
            break;
          }

          for (const component of addressComponents) {
            if (
              !locationName &&
              (component.types.includes('neighborhood') ||
                component.types.includes('sublocality') ||
                component.types.includes('sublocality_level_1'))
            ) {
              locationName = component.long_name;
              break;
            }
          }

          if (locationName) break;

          for (const component of addressComponents) {
            if (component.types.includes('locality')) {
              locationName = component.long_name;
              break;
            }
            if (!locationName && component.types.includes('administrative_area_level_2')) {
              locationName = component.long_name;
            }
            if (!locationName && component.types.includes('administrative_area_level_1')) {
              locationName = component.long_name;
            }
          }

          if (locationName) break;
        }

        if (!locationName && results[0].formatted_address) {
          const parts = results[0].formatted_address.split(',');
          locationName = parts[0]?.trim() || 'Unknown location';
        }

        setLocation({
          county: locationName || 'Unknown location',
          lat,
          lng,
        });
      }
    });
  }, []);

  const geolocationActiveRef = useRef(false);

  const cancelGeolocation = () => {
    geolocationActiveRef.current = false;
    setIsLocating(false);
  };

  // Only triggers the browser permission dialog — no GPS search, no spinner, no errors
  const requestPermission = () => {
    if (!navigator.geolocation || !window.isSecureContext) {
      showToast(t('locationSetup.turnOnLocationOrSelect'), 'error');
      return;
    }

    // Fire and forget — just triggers the permission dialog
    // The permission state change listener will update the button text
    navigator.geolocation.getCurrentPosition(
      () => {},
      () => {},
      { enableHighAccuracy: false, maximumAge: Infinity, timeout: 1 }
    );
  };

  // Actually searches for the user's GPS position. Uses the modality-aware
  // helper so a Telegram Mini-App user gets LocationManager (with their
  // grant against Telegram, not our domain) and PWA users get
  // navigator.geolocation as before.
  const findMyLocation = async () => {
    track('FE Location GPS Started');
    geolocationActiveRef.current = true;
    setIsLocating(true);

    try {
      const { getCurrentLocation } = await import('@/lib/location');
      const coords = await getCurrentLocation();
      if (!geolocationActiveRef.current) return;
      placeMarker({ lat: coords.latitude, lng: coords.longitude });
      reverseGeocode(coords.latitude, coords.longitude);
    } catch {
      if (!geolocationActiveRef.current) return;
      showToast(t('locationSetup.turnOnLocationOrSelect'), 'error');
    } finally {
      setIsLocating(false);
      geolocationActiveRef.current = false;
    }
  };

  const handleLocationButton = async () => {
    // In a Telegram Mini-App, navigator.permissions.query() reports the
    // browser-origin permission, which is never 'granted' for the
    // Telegram webview. The actual permission is held by Telegram (its
    // LocationManager has its own grant flow). Bypass the
    // requestPermission step entirely and let getCurrentLocation handle
    // the prompt — it'll surface a toast on denial via the existing
    // catch in findMyLocation.
    const { isTelegramWebApp } = await import('@/lib/platform');
    if (isTelegramWebApp() || locationPermission === 'granted') {
      findMyLocation();
    } else {
      requestPermission();
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (!isGoogleLoaded) return;

      try {
        const { AutocompleteSuggestion } = google.maps.places;
        const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: value,
        });

        setSuggestions(
          response.suggestions
            .filter((s: google.maps.places.Suggestion) => s.placePrediction)
            .map((s: google.maps.places.Suggestion) => ({
              placeId: s.placePrediction!.placeId,
              text: s.placePrediction!.text.toString(),
            }))
        );
      } catch {
        setSuggestions([]);
      }
    }, 300);
  };

  const handleSelectSuggestion = async (suggestion: PlaceSuggestion) => {
    track('FE Location Search Selected', { place: suggestion.text });
    cancelGeolocation();
    setSearchValue(suggestion.text);
    setSuggestions([]);

    try {
      const { Place } = google.maps.places;
      const place = new Place({ id: suggestion.placeId });
      await place.fetchFields({ fields: ['location', 'displayName'] });

      if (place.location) {
        const pos = { lat: place.location.lat(), lng: place.location.lng() };
        placeMarker(pos);
        reverseGeocode(pos.lat, pos.lng);
      }
    } catch {
      showToast('Could not load place details', 'error');
    }
  };

  const handleConfirm = async () => {
    if (!location) return;

    try {
      await updateLocationMutation.mutateAsync({
        lat: location.lat,
        long: location.lng,
        // Send the FE-resolved name so the BE doesn't lose it when its own
        // geocoder fails to classify the coords (e.g. Addis Ababa).
        municipality: location.county,
      });
      track('FE Location Confirmed', {
        municipality: location.county,
        method: 'existing',
      });
      // Small delay to ensure React Query cache update propagates
      setTimeout(() => {
        if (variant === 'update') {
          // Show the "Location updated" success sheet; Done closes + onSaved
          setModalState('saved');
        } else {
          onClose();
          onSaved?.();
        }
      }, 100);
    } catch {
      // Toast shown by axios interceptor - let user try again
    }
  };

  const handleChange = () => {
    track('FE Location Change Tapped');
    setPreviousModalState('confirm');
    setModalState('map');
  };

  const handleMapContinue = async () => {
    if (!location) return;

    try {
      await updateLocationMutation.mutateAsync({
        lat: location.lat,
        long: location.lng,
        municipality: location.county,
      });
      track('FE Location Confirmed', {
        municipality: location.county,
        method: 'map',
      });
      // Small delay to ensure React Query cache update propagates
      setTimeout(() => {
        if (variant === 'update') {
          // Show the "Location updated" success sheet; Done closes + onSaved
          setModalState('saved');
        } else {
          onClose();
          onSaved?.();
        }
      }, 100);
    } catch {
      // Toast shown by axios interceptor - let user try again
    }
  };

  const handleCancel = () => {
    if (previousModalState === 'confirm') {
      setModalState('confirm');
    } else {
      onClose();
    }
  };

  const handleSavedDone = () => {
    onClose();
    onSaved?.();
  };

  const isSaving = updateLocationMutation.isPending;

  return (
    <>
      {/* Confirm Modal */}
      <BottomSheet isOpen={modalState === 'confirm'} onClose={onClose}>
        <div className="text-center">
          <h2 className={`text-[20px] font-bold text-gray-900 ${variant === 'update' ? 'mb-1' : 'mb-4'}`}>
            {variant === 'update'
              ? t('locationPicker.stillYourLocation')
              : t('locationSetup.locationQuestion', { county: location?.county || '' })}
          </h2>

          {variant === 'update' && (
            <p className="text-[13px] text-gray-500 mb-4">
              {t('locationPicker.youSaved', { county: location?.county || '' })}
            </p>
          )}

          <div className="rounded-2xl px-5 py-4 mb-4 flex items-center justify-between bg-gray-100">
            <div className="flex items-center gap-3">
              <span className="text-gray-900 font-semibold">{location?.county}</span>
              <span className="text-lg">{weather ? getWeatherEmoji(weather.current.weather_code) : '🌤️'}</span>
            </div>
            {weather && (
              <span className="text-gray-900 text-[20px] font-semibold">{Math.round(weather.current.temperature)}°</span>
            )}
          </div>

          {variant !== 'update' && (
            <p className="text-[13px] text-gray-500 mb-6">{t('locationSetup.confirmHint')}</p>
          )}

          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSaving}
            className="w-full h-[46px] rounded-xl text-white text-[13px] font-semibold tracking-wide mb-3 disabled:opacity-60"
            style={{ backgroundColor: primaryColor }}
          >
            {isSaving
              ? t('common.loading')
              : variant === 'update'
                ? t('locationPicker.keepIt')
                : t('locationSetup.confirmLocation')}
          </button>

          <button
            type="button"
            onClick={handleChange}
            disabled={isSaving}
            className="w-full h-[46px] rounded-xl border border-gray-200 bg-white text-gray-900 text-[13px] font-semibold tracking-wide"
          >
            {t('locationSetup.changeLocation')}
          </button>
        </div>
      </BottomSheet>

      {/* Map Modal */}
      <BottomSheet isOpen={modalState === 'map'} onClose={handleCancel}>
        <div>
          {/* Title */}
          <h2 className="text-[20px] font-bold text-gray-900 text-center mb-4">
            {variant === 'update'
              ? t('locationSetup.updateYourLocation')
              : t('locationSetup.typePreferredLocation')}
          </h2>

          {/* Search */}
          <div className="relative mb-3">
            <div className="flex items-center rounded-xl border border-gray-200 bg-gray-50 px-3 h-[46px]">
              <Search className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('locationSetup.searchPlaceholder')}
                className="flex-1 bg-transparent text-[14px] text-gray-900 placeholder-gray-400 outline-none"
              />
              {searchValue && (
                <button
                  type="button"
                  onClick={() => { setSearchValue(''); setSuggestions([]); }}
                  className="ml-1 p-0.5"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>

            {suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl border border-gray-200 shadow-lg z-10 max-h-[160px] overflow-y-auto">
                {suggestions.map((s) => (
                  <button
                    key={s.placeId}
                    type="button"
                    onClick={() => handleSelectSuggestion(s)}
                    className="w-full px-3 py-2.5 text-left text-[13px] text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    {s.text}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Map */}
          <div className="relative mb-4">
            <div
              ref={mapRef}
              className="w-full h-[240px] rounded-xl overflow-hidden bg-gray-100"
            />
            {isLocating && (
              <div className="absolute inset-0 rounded-xl bg-black/30 flex items-center justify-center">
                <div className="bg-white rounded-xl px-4 py-3 flex items-center gap-2 shadow-lg">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                  <span className="text-[13px] text-gray-700 font-medium">{t('locationSetup.locating')}</span>
                </div>
              </div>
            )}
          </div>

          {/* Share Location Button */}
          <button
            type="button"
            onClick={handleLocationButton}
            disabled={isLocating}
            className="w-full flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white py-3 mb-6 disabled:opacity-60"
          >
            <MapPin className="w-5 h-5 text-brand-primary" />
            <span className="text-[14px] text-gray-700 font-medium">
              {isLocating
                ? t('locationSetup.locating')
                : locationPermission === 'granted'
                  ? t('locationSetup.findMyLocation')
                  : t('locationSetup.orShareLocation')}
            </span>
          </button>

          {/* Bottom Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="h-[50px] px-6 rounded-xl border border-gray-200 bg-white text-gray-900 text-[13px] font-semibold tracking-wide"
            >
              {t('common.cancel')}
            </button>

            <button
              type="button"
              onClick={handleMapContinue}
              disabled={isSaving || !location}
              className="flex-1 h-[50px] rounded-xl text-white text-[14px] font-semibold tracking-wide disabled:opacity-60"
              style={{ backgroundColor: primaryColor }}
            >
              {isSaving
                ? t('common.loading')
                : variant === 'update'
                  ? t('common.continue')
                  : t('locationSetup.confirmLocation')}
            </button>
          </div>
        </div>
      </BottomSheet>

      {/* Saved confirmation (update variant) */}
      <BottomSheet isOpen={modalState === 'saved'} onClose={handleSavedDone}>
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3 text-2xl">
            ✅
          </div>
          <h2 className="text-[20px] font-bold text-gray-900 mb-1">
            {t('locationPicker.savedTitle')}
          </h2>
          <p className="text-[13px] text-gray-500 mb-5">
            {t('locationPicker.savedBody', { county: location?.county || '' })}
          </p>
          <button
            type="button"
            onClick={handleSavedDone}
            className="w-full h-[46px] rounded-xl text-white text-[13px] font-semibold tracking-wide"
            style={{ backgroundColor: primaryColor }}
          >
            {t('common.done')}
          </button>
        </div>
      </BottomSheet>
    </>
  );
}
